'use client'

import { motion } from 'motion/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useWindowSize } from 'react-use'
import { IDetailRestaurant } from '@/api/restaurants/types/restaurant-types'
import { Button } from '@/components/button'
import { FadeOverlay } from '@/components/fade-overlay'
import { PaginationDots } from '@/components/pagination-dots'
import { Skeleton } from '@/components/skeleton'
import { RestaurantDetailImage } from '@/features/restaurants/restaurant-detail/restaurant-detail-images/restaurant-detail-image'
import { SvgOutlinedChevronRight } from '@/icons/svg-outlined-chevron-right'
import { debounce } from '@/utils/functions/debounce'
import { range } from '@/utils/functions/range'
import { throttle } from '@/utils/functions/throttle'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'
import { useMobile } from '@/utils/window/use-mobile'
import { RestaurantDetailVideo } from './restaurant-detail-video'

interface IRestaurantDetailVisualsContainerProps {
    visuals: IDetailRestaurant['visuals'] | null
    className?: string
    forceMobile?: boolean
}

const DESKTOP_NUMBER_OF_VISUALS = 3
const DESKTOP_SPACING_BETWEEN_VISUALS = 8
const DESKTOP_ASPECT_RATIO = 16 / 9
const MOBILE_ASPECT_RATIO = 1 / 1

const DEFINED_MOBILE_WIDTH = 430
const DEFINED_DESKTOP_WINDOW_WIDTHS = [768, 1024, 1440, 1920, 2560]

type IRestaurantVisual = NonNullable<IDetailRestaurant['visuals']>[number]

export interface IEnhancedRestaurantVisual extends IRestaurantVisual {
    index: number | null
    isDuplicate: boolean
}

export const RestaurantDetailVisualsContainer = ({
    visuals,
    className,
    forceMobile,
}: IRestaurantDetailVisualsContainerProps) => {
    const rawIsMobile = useMobile()

    const isMobile = forceMobile ?? rawIsMobile

    const actualVisualsCount = visuals?.length ?? 0

    const adjustedRestaurantVisuals = useMemo<IEnhancedRestaurantVisual[]>(() => {
        // If there are less than three images, we want to duplicate the images to fill the space

        if (!visuals) return []

        if (visuals.length >= 3) {
            return visuals.map((visual, index) => ({
                ...visual,
                index,
                isDuplicate: false,
            }))
        }

        if (visuals.length === 2) {
            return [
                { ...visuals[0], index: 0, isDuplicate: false },
                { ...visuals[1], index: 1, isDuplicate: false },
                { ...visuals[0], index: null, isDuplicate: true },
            ]
        }

        if (visuals.length === 1) {
            return [
                { ...visuals[0], index: 0, isDuplicate: false },
                { ...visuals[0], index: null, isDuplicate: true },
                { ...visuals[0], index: null, isDuplicate: true },
            ]
        }

        return []
    }, [visuals])

    const scrollContainerRef = useRef<HTMLDivElement | null>(null)

    const [currentVisualPage, setCurrentVisualPage] = useState(1)

    const numberOfVisualPages = useMemo(() => {
        if (isMobile) return actualVisualsCount

        return Math.max(actualVisualsCount - DESKTOP_NUMBER_OF_VISUALS, 1)
    }, [actualVisualsCount, isMobile])

    const refs = useRef<{ [index: number]: HTMLDivElement | null }>({})

    const makeRef = useCallback(
        (index: number) => (element: HTMLDivElement | null) => {
            refs.current[index] = element
        },
        []
    )

    const { width: windowWidth } = useWindowSize()

    const [{ visualHeight, visualWidth }, setVisualSize] = useState({
        visualHeight: 0,
        visualWidth: 0,
    })

    const [{ visualContainerHeight, visualContainerWidth }, setVisualContainerSize] = useState({
        visualContainerHeight: 0,
        visualContainerWidth: 0,
    })

    const onScroll = useMemo(() => {
        return throttle(() => {
            const container = scrollContainerRef.current

            if (!container) return

            const actualVisualWidth = isMobile ? container.clientWidth : visualWidth
            const currentPage = Math.round(container.scrollLeft / actualVisualWidth) + 1

            if (currentPage !== currentVisualPage && currentPage <= numberOfVisualPages) {
                setCurrentVisualPage(currentPage)
            }
        }, 300)
    }, [currentVisualPage, visualWidth, isMobile, numberOfVisualPages])

    const calculateVisualHeight = useCallback((windowWidth: number, visualWidth: number) => {
        return visualWidth / (windowWidth < 768 ? MOBILE_ASPECT_RATIO : DESKTOP_ASPECT_RATIO)
    }, [])

    const calculatePerfectVisualSize = useCallback(
        (windowWidth: number) => {
            const width =
                windowWidth / DESKTOP_NUMBER_OF_VISUALS -
                ((DESKTOP_NUMBER_OF_VISUALS - 1) / DESKTOP_NUMBER_OF_VISUALS) *
                    DESKTOP_SPACING_BETWEEN_VISUALS

            const height = calculateVisualHeight(windowWidth, width)

            return { height, width }
        },
        [calculateVisualHeight]
    )

    const rawOnWindowResize = useCallback(() => {
        // Calculate the width of the images. We want to have 3 images in a row with a gap in between them

        const windowWidth = typeof window !== 'undefined' ? window.innerWidth : undefined

        if (typeof windowWidth !== 'number') return

        const isMobile = windowWidth < 768

        if (isMobile) {
            setVisualSize({
                visualWidth: DEFINED_MOBILE_WIDTH,
                visualHeight: DEFINED_MOBILE_WIDTH / MOBILE_ASPECT_RATIO,
            })

            setVisualContainerSize({
                visualContainerHeight: windowWidth / MOBILE_ASPECT_RATIO,
                visualContainerWidth: windowWidth,
            })

            return
        }

        const adjustedWindowWidth =
            DEFINED_DESKTOP_WINDOW_WIDTHS.find((width) => width >= windowWidth) ??
            DEFINED_DESKTOP_WINDOW_WIDTHS[DEFINED_DESKTOP_WINDOW_WIDTHS.length - 1]

        const perfectVisualSize = calculatePerfectVisualSize(windowWidth)

        const adjustedVisualSize = calculatePerfectVisualSize(adjustedWindowWidth)

        if (!adjustedVisualSize) return

        // Calculate the height of the images based on the width and the aspect ratio

        setVisualContainerSize({
            visualContainerHeight: Math.round(perfectVisualSize.height),
            visualContainerWidth: Math.round(perfectVisualSize.width),
        })

        setVisualSize({
            visualHeight: Math.round(adjustedVisualSize.height),
            visualWidth: Math.round(adjustedVisualSize.width),
        })
    }, [calculatePerfectVisualSize])

    const debouncedOnWindowResize = useMemo(
        () => debounce(rawOnWindowResize, 100),
        [rawOnWindowResize]
    )

    const hasSyncedWindowInitially = useRef(false)
    useEffect(() => {
        if (windowWidth === undefined) return

        if (!hasSyncedWindowInitially.current) {
            rawOnWindowResize()
            hasSyncedWindowInitially.current = true

            return
        }

        debouncedOnWindowResize()
    }, [windowWidth, debouncedOnWindowResize, rawOnWindowResize])

    /**
     * There was a really nasty bug on mobile, where the first image to show was not always the first image in the list, but a random one.
     * Essentially this was because, while resizing, the snap behaviour of the scroll container hooked into the initial resize event and made the scroll snap to the nearest image.
     * To solve this, we wait for a brief moment until we append the snap styles to the scroll container.
     */
    const [hasSyncedInitialScroll, setHasSyncedInitialScroll] = useState(false)
    useEffect(() => {
        if (!scrollContainerRef.current) return

        const timeout = setTimeout(() => {
            setHasSyncedInitialScroll(true)
        }, 500)

        return () => clearTimeout(timeout)
    }, [])

    const isDesktopNextVisualButtonDisabled = currentVisualPage === numberOfVisualPages
    const isDesktopPreviousVisualButtonDisabled = currentVisualPage === 1

    const onDesktopPaginationButtonClick = useCallback(
        (direction: 'next' | 'previous') => () => {
            if (!scrollContainerRef.current) return

            const container = scrollContainerRef.current

            const nextLeft =
                direction === 'next'
                    ? container.scrollLeft + visualWidth
                    : container.scrollLeft - visualWidth

            container.scrollTo({
                behavior: 'smooth',
                left: nextLeft,
            })
        },
        [visualWidth]
    )

    return (
        <div className={cn('block max-w-[100vw] overflow-hidden relative', className)}>
            <motion.div
                initial={{ opacity: isDesktopPreviousVisualButtonDisabled ? 0 : 1 }}
                animate={{ opacity: isDesktopPreviousVisualButtonDisabled ? 0 : 1 }}
                transition={{ duration: 0.1, ease: 'easeOut' }}
                className="hidden md:block absolute transform-center-y left-10 z-[1] w-fit shadow-custom rounded-full"
                style={{ cursor: isDesktopPreviousVisualButtonDisabled ? 'default' : 'pointer' }}
            >
                <div className="rotate-180">
                    <Button
                        shape="circle"
                        styleType="secondary"
                        backgroundColor="white"
                        disabled={isDesktopPreviousVisualButtonDisabled}
                        onClick={onDesktopPaginationButtonClick('previous')}
                        height={'48px'}
                        width={'48px'}
                        className="hidden md:flex"
                    >
                        <SvgOutlinedChevronRight color={COLORS.black} size={32} />
                    </Button>
                </div>
            </motion.div>

            <div
                ref={scrollContainerRef}
                key={'scroll-container-restaurants'}
                onScroll={onScroll}
                className={cn(
                    'scrollbar-hide restaurant-detail-images-container md:overflow-y-hidden',
                    hasSyncedInitialScroll && 'snap-x snap-mandatory'
                )}
            >
                {run(() => {
                    if (!adjustedRestaurantVisuals?.length) {
                        return range(3).map((_, index) => {
                            return (
                                <div
                                    key={index + 'restaurant-page' + 'skeleton'}
                                    className="restaurant-detail-images-skeleton"
                                >
                                    <Skeleton className="w-full h-full" />
                                </div>
                            )
                        })
                    }

                    return adjustedRestaurantVisuals.map((visual, rawIndex) => {
                        const firstVideoIndex = adjustedRestaurantVisuals.findIndex(
                            (visual) => visual.type === 'VIDEO'
                        )
                        if (visual.type === 'IMAGE') {
                            return (
                                <RestaurantDetailImage
                                    url={visual.visualUrl!}
                                    isDuplicate={visual?.isDuplicate}
                                    index={rawIndex}
                                    imageContainerHeight={visualContainerHeight}
                                    imageContainerWidth={visualContainerWidth}
                                    imageHeight={visualHeight}
                                    imageWidth={visualWidth}
                                    makeRef={makeRef}
                                    key={visual.url + 'restaurant-page' + rawIndex}
                                />
                            )
                        } else if (visual.type === 'VIDEO') {
                            return (
                                <RestaurantDetailVideo
                                    url={visual.visualUrl!}
                                    thumbnailUrl={visual?.thumbnailUrl}
                                    isDuplicate={visual?.isDuplicate}
                                    index={rawIndex}
                                    videoContainerHeight={visualContainerHeight}
                                    videoContainerWidth={visualContainerWidth}
                                    videoHeight={visualHeight}
                                    videoWidth={visualWidth}
                                    autoPlay={
                                        (firstVideoIndex === rawIndex &&
                                            rawIndex < 3 &&
                                            !isMobile) ||
                                        (rawIndex === 0 && isMobile && firstVideoIndex === rawIndex)
                                    }
                                    makeRef={makeRef}
                                    key={visual.url + 'restaurant-page' + rawIndex}
                                />
                            )
                        }
                    })
                })}
            </div>

            <motion.div
                initial={{ opacity: isDesktopNextVisualButtonDisabled ? 0 : 1 }}
                animate={{ opacity: isDesktopNextVisualButtonDisabled ? 0 : 1 }}
                transition={{ duration: 0.1, ease: 'easeOut' }}
                className="hidden md:block absolute transform-center-y right-10 z-[1] w-fit shadow-custom rounded-full"
                style={{ cursor: isDesktopNextVisualButtonDisabled ? 'default' : 'pointer' }}
            >
                <Button
                    shape="circle"
                    styleType="secondary"
                    backgroundColor="white"
                    disabled={isDesktopNextVisualButtonDisabled}
                    onClick={onDesktopPaginationButtonClick('next')}
                    height={'48px'}
                    width={'48px'}
                    className="hidden md:flex"
                >
                    <SvgOutlinedChevronRight color={COLORS.black} size={32} />
                </Button>
            </motion.div>

            <div className="md:hidden absolute top-4 right-8 w-[70vw]">
                <PaginationDots
                    currentPage={currentVisualPage}
                    pagesCount={actualVisualsCount}
                    className="justify-start"
                />
            </div>

            <FadeOverlay className="md:hidden absolute bottom-[-2px]" />
        </div>
    )
}
